/**
 * CostByServiceCard — custo por serviço AWS (SP3).
 *
 * BarChart Mantine na paleta teal do DS (`inovareTeal`). Cada barra é clicável
 * (`onSelect(service)`) para cross-filter (RF4). Estado vazio → EmptyState `bare`.
 * Selo de dados parciais quando `is_partial`.
 *
 * Não faz fetch: recebe `BreakdownResponse` por prop. Cores apenas via tokens DS.
 */
import { Box } from '@mantine/core'
import { BarChart } from '@mantine/charts'
import { IconCloud } from '@tabler/icons-react'

import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { formatUSD } from '@/utils/finops'
import type { BreakdownResponse } from '@/api/finopsDashboard'

function compactUSD(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}k`
  return `$${value.toFixed(0)}`
}

export interface CostByServiceCardProps {
  data: BreakdownResponse
  /** Cross-filter: chamado com o serviço da barra clicada (RF4). */
  onSelect?: (service: string) => void
  /** Limite de serviços exibidos. Default 15. */
  limit?: number
  title?: string
}

export function CostByServiceCard({
  data,
  onSelect,
  limit = 15,
  title,
}: CostByServiceCardProps) {
  const chartData = data.items.slice(0, limit).map((it) => ({
    service: it.key,
    Total: it.amount_usd,
  }))

  const resolvedTitle =
    title ??
    `Custo por serviço AWS${chartData.length > 0 ? `, top ${chartData.length}` : ''}`

  return (
    <WidgetCard accent="primary" p={0}>
      <CardHeader
        title={resolvedTitle}
        badge={data.is_partial ? { value: 'Parcial', color: 'inovareTeal' } : undefined}
      />
      <Box p="md">
        {chartData.length === 0 ? (
          <EmptyState
            icon={IconCloud}
            title="Sem dados por serviço"
            description="Não há custo por serviço no escopo e período selecionados."
            accent="info"
            size="sm"
            bare
          />
        ) : (
          <BarChart
            h={Math.max(280, chartData.length * 28)}
            data={chartData}
            dataKey="service"
            orientation="vertical"
            yAxisProps={{ width: 160 }}
            series={[{ name: 'Total', color: 'inovareTeal.7' }]}
            tickLine="xy"
            gridAxis="x"
            valueFormatter={(v) => compactUSD(Number(v))}
            withTooltip
            tooltipProps={{
              content: ({ payload }) => {
                const p = payload?.[0]
                if (!p) return null
                return (
                  <Box
                    p="xs"
                    style={{
                      background: 'var(--mantine-color-body)',
                      border: '1px solid var(--ai-border-light)',
                      borderRadius: 'var(--mantine-radius-sm)',
                    }}
                  >
                    {String(p.payload.service)}: {formatUSD(Number(p.value))}
                  </Box>
                )
              },
            }}
            barProps={
              onSelect
                ? {
                    cursor: 'pointer',
                    onClick: (entry: { service?: string }) => {
                      if (entry?.service) onSelect(entry.service)
                    },
                  }
                : undefined
            }
          />
        )}
      </Box>
    </WidgetCard>
  )
}
